import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Typography,
  Image,
  Tag,
  Space,
  Input,
  Button,
  Modal,
  Descriptions,
  Tooltip,
  App,
  Select,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api from '@/api/client';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// 图片文件服务器配置
const STORAGE_BASE_URL = '/api/storage';

interface ProjectImage {
  id: number;
  project_id: number;
  user_id: number;
  scene_id: string;
  prompt: string;
  params: string;
  file_path: string;
  file_size: number;
  width: number;
  height: number;
  format: string;
  quality: string;
  quality_tier: string;
  preview_path: string;
  status: string;
  is_favorite: number;
  tags: string;
  generation_time_ms: number;
  created_at: string;
  updated_at: string;
  username: string;
  user_display_name: string;
  project_name: string;
  scene_name: string;
  scene_group_name: string;
  scene_phase_name: string;
}

// 场景ID到中文名称的映射（从数据库scene_charts表获取）
const SCENE_NAME_MAP: Record<string, string> = {
  // 产品规划阶段
  'product-planning': '产品规划',
  'competitor_analysis': '竞品分析',
  'user_research': '用户研究',
  'product_positioning': '产品定位',
  // 产品设计阶段
  'feature_design': '功能设计',
  'ui_design': 'UI设计',
  'interaction_design': '交互设计',
  // 数据分析阶段
  'data_analysis': '数据分析',
  'data-abtest': 'A/B测试',
  'data-flow': '用户流程',
  'competitive-matrix': '竞争矩阵',
  'competitive-position': '竞争定位',
  'competitive-radar': '竞争雷达',
  'competitive-swot': 'SWOT分析',
  // 营销策略阶段
  'marketing_strategy': '营销策略',
  'growth_hacking': '增长黑客',
  'user_onboarding': '用户引导',
  'product_launch': '产品发布',
  // 通用场景
  'chat': 'AI对话',
  'image_gen': '图片生成',
  'default': '默认场景',
};

// 质量等级中文映射
const QUALITY_TIER_MAP: Record<string, string> = {
  'standard': '标准',
  'hd': '高清',
};

// 状态中文映射
const STATUS_MAP: Record<string, { text: string; color: string }> = {
  'active': { text: '正常', color: 'green' },
  'archived': { text: '已归档', color: 'orange' },
  'deleted': { text: '已删除', color: 'red' },
};

export default function ImagesPage() {
  const [data, setData] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailModal, setDetailModal] = useState<{ open: boolean; record?: ProjectImage }>({ open: false });
  const { message } = App.useApp();

  const loadData = async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await api.get('/images', {
        params: { 
          page: currentPage, 
          pageSize, 
          keyword,
          status: statusFilter || undefined,
        },
      });
      setData(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      message.error(err.response?.data?.error || '获取图片列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    loadData(1);
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // 获取完整的图片URL
  const getImageUrl = (record: ProjectImage) => {
    const path = record.preview_path || record.file_path;
    if (!path) return '';
    // 如果 path 已经是完整URL，直接返回
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // 否则拼接 storage API 路径
    return `${STORAGE_BASE_URL}/${path}`;
  };

  // 获取场景中文名称
  const getSceneName = (sceneId: string) => {
    if (!sceneId) return '-';
    return SCENE_NAME_MAP[sceneId] || sceneId;
  };

  // 获取质量等级中文
  const getQualityTierText = (tier: string) => {
    return QUALITY_TIER_MAP[tier] || tier || '-';
  };

  // 获取状态中文
  const getStatusTag = (status: string) => {
    const config = STATUS_MAP[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '预览',
      width: 80,
      render: (_: any, record: ProjectImage) => {
        const url = getImageUrl(record);
        return (
          <Image
            src={url}
            alt="预览"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            placeholder={true}
          />
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '用户',
      width: 120,
      render: (_: any, record: ProjectImage) => (
        <div>
          <div>{record.user_display_name || record.username || '-'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>ID: {record.user_id}</Text>
        </div>
      ),
    },
    {
      title: '场景',
      width: 240,
      render: (_: any, record: ProjectImage) => {
        const phase = record.scene_phase_name || '-';
        const group = record.scene_group_name || '-';
        const scene = record.scene_name || getSceneName(record.scene_id) || '-';
        return (
          <Tooltip title={`${phase} > ${group} > ${scene}`}>
            <div style={{ fontSize: 12 }}>
              <div style={{ color: '#999' }}>{phase}</div>
              <div style={{ color: '#666', marginLeft: 8 }}>{group}</div>
              <div style={{ color: '#333', marginLeft: 16, fontWeight: 500 }}>{scene}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '提示词',
      dataIndex: 'prompt',
      ellipsis: true,
      width: 200,
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft">
          <span style={{ cursor: 'pointer' }}>{v?.substring(0, 30) || '-'}{v?.length > 30 ? '...' : ''}</span>
        </Tooltip>
      ),
    },
    {
      title: '规格',
      width: 100,
      render: (_: any, record: ProjectImage) => (
        <div>
          <div>{record.width && record.height ? `${record.width}×${record.height}` : '-'}</div>
          <Tag color="blue">{getQualityTierText(record.quality_tier)}</Tag>
        </div>
      ),
    },
    {
      title: '质量',
      dataIndex: 'quality',
      width: 80,
      render: (v: string) => v || '-',
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      width: 80,
      render: formatFileSize,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: getStatusTag,
    },
    {
      title: '耗时',
      dataIndex: 'generation_time_ms',
      width: 80,
      render: (v: number) => {
        if (!v) return '-';
        if (v < 1000) return `${v}ms`;
        return `${(v / 1000).toFixed(1)}s`;
      },
    },
    {
      title: '生成时间',
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => v ? dayjs(v).format('MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: ProjectImage) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setDetailModal({ open: true, record })}
        >
          详情
        </Button>
      ),
    },
  ];

  const renderDetailModal = () => {
    const record = detailModal.record;
    if (!record) return null;

    let paramsObj: any = {};
    try {
      paramsObj = JSON.parse(record.params || '{}');
    } catch {
      paramsObj = {};
    }

    const imageUrl = getImageUrl(record);

    return (
      <Modal
        title="图片详情"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false })}
        width={900}
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center', background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
            <Image
              src={imageUrl}
              alt="图片预览"
              style={{ maxHeight: 400, objectFit: 'contain' }}
            />
          </div>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="ID">{record.id}</Descriptions.Item>
            <Descriptions.Item label="用户">{record.user_display_name || record.username || '-'} (ID: {record.user_id})</Descriptions.Item>
            <Descriptions.Item label="项目">{record.project_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="场景">{record.scene_phase_name || '-'} &gt; {record.scene_group_name || '-'} &gt; {record.scene_name || getSceneName(record.scene_id) || '-'}</Descriptions.Item>
            <Descriptions.Item label="尺寸">{record.width && record.height ? `${record.width} × ${record.height} px` : '-'}</Descriptions.Item>
            <Descriptions.Item label="格式">{record.format?.toUpperCase() || '-'}</Descriptions.Item>
            <Descriptions.Item label="质量">{record.quality || '-'}</Descriptions.Item>
            <Descriptions.Item label="质量等级">{getQualityTierText(record.quality_tier)}</Descriptions.Item>
            <Descriptions.Item label="文件大小">{formatFileSize(record.file_size)}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(record.status)}</Descriptions.Item>
            <Descriptions.Item label="收藏">{record.is_favorite ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="生图耗时">{record.generation_time_ms ? (record.generation_time_ms < 1000 ? `${record.generation_time_ms}ms` : `${(record.generation_time_ms / 1000).toFixed(1)}s`) : '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
          </Descriptions>
          <Card title="提示词 (Prompt)" size="small">
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{record.prompt || '-'}</pre>
          </Card>
          {Object.keys(paramsObj).length > 0 && (
            <Card title="生成参数" size="small">
              <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(paramsObj, null, 2)}</pre>
            </Card>
          )}
          <Card title="文件路径" size="small">
            <Text copyable style={{ fontSize: 12 }}>{record.file_path}</Text>
            {record.preview_path && record.preview_path !== record.file_path && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>预览路径: </Text>
                <Text copyable style={{ fontSize: 12 }}>{record.preview_path}</Text>
              </div>
            )}
          </Card>
        </Space>
      </Modal>
    );
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>图片管理</Title>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="搜索提示词、用户名、项目名"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280 }}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="状态筛选"
            value={statusFilter || undefined}
            onChange={(v) => { setStatusFilter(v || ''); setPage(1); }}
            style={{ width: 120 }}
            allowClear
            options={[
              { label: '正常', value: 'active' },
              { label: '已归档', value: 'archived' },
              { label: '已删除', value: 'deleted' },
            ]}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setKeyword(''); setStatusFilter(''); setPage(1); loadData(1); }}>刷新</Button>
        </Space>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps || 20); },
          }}
        />
      </Card>
      {renderDetailModal()}
    </div>
  );
}
